/**
 * LeadsTableView — ops scan table.
 * Columns capped at ~1/6 viewport; truncated text uses ellipsis + tooltip.
 */

import React, { useMemo, useState } from "react";
import { Typography, Button, Tag, Switch, Space, Tooltip } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { Box, CircularProgress } from "@mui/material";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import DataTable from "../../../shared/components/DataTable";
import FollowUpMention from "../../followups/components/FollowUpMention.jsx";
import OriginBadge from "./OriginBadge.jsx";
import OperationalStatusBadge from "./OperationalStatusBadge.jsx";
import LeadRiskChips from "./LeadRiskChips.jsx";
import {
  formatLeadQuoteValue,
  getLeadEngine,
  getLeadProviderStatus,
  getLeadQuoteValue,
  getLeadTruckRecommendation,
  getLeadTruckLoadLabel,
} from "../services/leadOperationalStatus.js";
import {
  formatLeadCreatedDisplay,
  groupLeadsByCreatedPeriod,
} from "../services/leadCreatedPeriod.js";
import {
  formatTruckLabelWithLoad,
  getLeadBillableVolumeM3,
  getLeadCorridorBadge,
  getLeadDistanceKm,
  getLeadPriorityLabel,
} from "../services/leadJobCockpit.js";

const { Text, Title } = Typography;

/** Never let a column grow past one-sixth of the viewport. */
const COL_MAX = "16.67vw";

const ellipsisSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: "100%",
  minWidth: 0,
};

const formatPhone = (phone) => {
  if (!phone) return "N/A";
  return phone.replace("+27", "0");
};

const PRIORITY_COLOR = {
  High: "#e65100",
  Medium: "#f9a825",
  Low: "#757575",
};

const ENGINE_TAG_COLOR = {
  shared_load: "blue",
  local: "cyan",
  intra_province: "purple",
  special: "magenta",
  unknown: "default",
};

const EllipsisText = ({ title, children, style, type, strong }) => (
  <Tooltip title={title ?? children}>
    <Box component="span" sx={{ display: "block", ...ellipsisSx, ...style }}>
      <Text type={type} strong={strong} style={{ fontSize: "inherit", color: "inherit" }}>
        {children}
      </Text>
    </Box>
  </Tooltip>
);

const EllipsisTag = ({ title, children, color, style }) => (
  <Tooltip title={title ?? children}>
    <Box component="span" sx={{ display: "inline-block", maxWidth: "100%", minWidth: 0 }}>
      <Tag
        color={color}
        style={{
          marginInlineEnd: 0,
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          verticalAlign: "middle",
          ...style,
        }}
      >
        {children}
      </Tag>
    </Box>
  </Tooltip>
);
const cellStack = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  minWidth: 0,
  maxWidth: "100%",
  overflow: "hidden",
};

const LeadsTableView = ({
  leads = [],
  loading = false,
  onViewLead,
  onRefresh,
  onFollowUpNavigate,
  onRouteClick,
  enableRowSelection = false,
  selectedRowKeys = [],
  onSelectionChange,
  onRowClick,
  groupByCreatedPeriod: groupByCreatedPeriodProp,
}) => {
  const [groupByCreated, setGroupByCreated] = useState(
    groupByCreatedPeriodProp ?? true
  );

  const columns = useMemo(
    () => [
      {
        title: "Lead",
        key: "lead",
        width: COL_MAX,
        maxWidth: COL_MAX,
        minWidth: 120,
        render: (_, record) => {
          const name = record.name || "Untitled lead";
          const phone = formatPhone(record.phone);
          return (
            <Box sx={cellStack}>
              <EllipsisText title={name} style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.35 }}>
                {name}
              </EllipsisText>
              {record.referenceNumber && (
                <EllipsisText
                  title={record.referenceNumber}
                  type="secondary"
                  style={{ fontSize: 11 }}
                >
                  {record.referenceNumber}
                </EllipsisText>
              )}
              {record.providerPricing?.status === "awaiting_provider" ? (
                <EllipsisTag title="Awaiting provider price" color="orange">
                  Not priced
                </EllipsisTag>
              ) : null}
              <EllipsisText title={phone} type="secondary" style={{ fontSize: 11 }}>
                {phone}
              </EllipsisText>
              <OriginBadge lead={record} size="small" />
            </Box>
          );
        },
      },
      {
        title: "Route",
        key: "route",
        width: COL_MAX,
        maxWidth: COL_MAX,
        minWidth: 140,
        render: (_, record) => {
          const from = record.moving_from || record.from;
          const to = record.moving_to || record.to;
          const km = getLeadDistanceKm(record);
          const routeLabel =
            from && to ? `${from} → ${to}` : "Route pending";
          const engine = getLeadEngine(record);
          const corridor = getLeadCorridorBadge(record);
          const distanceLabel = km != null ? `${Math.round(km)} km` : null;
          const moveTip = [engine.label, corridor, distanceLabel]
            .filter(Boolean)
            .join(" · ");
          const tip = [routeLabel, moveTip].filter(Boolean).join("\n");

          return (
            <Box sx={cellStack}>
              <Box
                sx={{
                  minWidth: 0,
                  maxWidth: "100%",
                  overflow: "hidden",
                  cursor: onRouteClick && from && to ? "pointer" : "default",
                  color: onRouteClick && from && to ? "#1890ff" : undefined,
                }}
                onClick={(e) => {
                  if (!onRouteClick || !from || !to) return;
                  e.stopPropagation();
                  onRouteClick(record, e);
                }}
              >
                <EllipsisText
                  title={tip}
                  type={!from || !to ? "secondary" : undefined}
                  style={{
                    fontWeight: from && to ? 500 : 400,
                    fontSize: 13,
                    lineHeight: 1.35,
                    color: "inherit",
                  }}
                >
                  {routeLabel}
                </EllipsisText>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  minWidth: 0,
                  maxWidth: "100%",
                  overflow: "hidden",
                }}
              >
                {distanceLabel && (
                  <EllipsisTag color="default" title={distanceLabel}>
                    {distanceLabel}
                  </EllipsisTag>
                )}
                <EllipsisTag
                  color={ENGINE_TAG_COLOR[engine.key] || "default"}
                  title={engine.label}
                >
                  {engine.label}
                </EllipsisTag>
                {corridor && (
                  <EllipsisTag color="geekblue" title={corridor}>
                    {corridor}
                  </EllipsisTag>
                )}
              </Box>
            </Box>
          );
        },
      },
      {
        title: "Value",
        key: "value",
        width: 88,
        maxWidth: COL_MAX,
        minWidth: 72,
        align: "right",
        render: (_, record) => {
          const value = formatLeadQuoteValue(getLeadQuoteValue(record));
          return (
            <EllipsisText title={value} strong style={{ fontSize: 13 }}>
              {value}
            </EllipsisText>
          );
        },
      },
      {
        title: "Truck",
        key: "truck",
        width: 140,
        maxWidth: COL_MAX,
        minWidth: 100,
        render: (_, record) => {
          const size = getLeadTruckRecommendation(record);
          const load = getLeadTruckLoadLabel(record);
          const truck = formatTruckLabelWithLoad(size, load);
          const volumeM3 = getLeadBillableVolumeM3(record);
          const volumeLabel =
            volumeM3 != null
              ? `${Number(volumeM3).toFixed(volumeM3 >= 10 ? 1 : 2)} m³`
              : null;
          if (truck === "—" && !volumeLabel) {
            return <Text type="secondary">—</Text>;
          }
          const tip = [truck !== "—" ? truck : null, volumeLabel]
            .filter(Boolean)
            .join(" · ");
          return (
            <Tooltip title={tip}>
              <Box sx={cellStack}>
                {truck !== "—" && (
                  <EllipsisTag title={truck}>{truck}</EllipsisTag>
                )}
                {volumeLabel && (
                  <EllipsisTag color="default" title={volumeLabel}>
                    {volumeLabel}
                  </EllipsisTag>
                )}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        title: "Status",
        key: "opsStatus",
        width: 120,
        maxWidth: COL_MAX,
        minWidth: 96,
        render: (_, record) => <OperationalStatusBadge lead={record} />,
      },
      {
        title: "Provider",
        key: "provider",
        width: 110,
        maxWidth: COL_MAX,
        minWidth: 88,
        render: (_, record) => {
          const provider = getLeadProviderStatus(record);
          return (
            <EllipsisTag title={provider.label}>{provider.label}</EllipsisTag>
          );
        },
      },
      {
        title: "Priority",
        key: "priority",
        width: 80,
        maxWidth: COL_MAX,
        minWidth: 72,
        render: (_, record) => {
          const priority = getLeadPriorityLabel(record);
          return (
            <Tooltip title={priority}>
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                sx={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}
              >
                <WhatshotIcon
                  sx={{
                    fontSize: 14,
                    color: PRIORITY_COLOR[priority],
                    flexShrink: 0,
                  }}
                />
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: PRIORITY_COLOR[priority],
                    ...ellipsisSx,
                  }}
                >
                  {priority}
                </Text>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        title: "Created",
        key: "createdAt",
        width: 100,
        maxWidth: COL_MAX,
        minWidth: 88,
        render: (_, record) => {
          const display = formatLeadCreatedDisplay(record);
          return (
            <EllipsisText
              title={display.secondary || display.primary}
              style={{ fontSize: 11 }}
            >
              {display.primary}
            </EllipsisText>
          );
        },
      },
      {
        title: "Risk",
        key: "risk",
        width: 90,
        maxWidth: COL_MAX,
        minWidth: 72,
        render: (_, record) => <LeadRiskChips lead={record} max={1} />,
      },
      {
        title: "Follow-up",
        key: "followUps",
        width: 72,
        maxWidth: COL_MAX,
        minWidth: 64,
        render: (_, record) => (
          <FollowUpMention
            summary={record?.followUpSummary}
            onClick={
              onFollowUpNavigate
                ? (e) => {
                    e?.stopPropagation?.();
                    onFollowUpNavigate(record);
                  }
                : undefined
            }
          />
        ),
      },
    ],
    [onFollowUpNavigate, onRouteClick]
  );

  const periodGroups = useMemo(
    () => (groupByCreated ? groupLeadsByCreatedPeriod(leads) : null),
    [groupByCreated, leads]
  );

  const groupToggle = (
    <Space size="small">
      <Text type="secondary" style={{ fontSize: 12 }}>
        Group by created
      </Text>
      <Switch
        size="small"
        checked={groupByCreated}
        onChange={setGroupByCreated}
      />
      {onRefresh && (
        <Button icon={<ReloadOutlined />} onClick={onRefresh} size="small">
          Refresh
        </Button>
      )}
    </Space>
  );

  const tableProps = {
    columns,
    loading,
    searchPlaceholder: "Search leads...",
    searchFields: [
      "name",
      "phone",
      "referenceNumber",
      "moving_from",
      "moving_to",
      "status",
      "notes",
    ],
    onRefresh,
    rowKey: (row) => row._id || row.id,
    enableRowSelection,
    selectedRowKeys,
    onSelectionChange,
    getCheckboxProps: (record) => ({
      disabled: record.status === "deleted",
    }),
    onRowClick: onRowClick ?? (enableRowSelection ? undefined : onViewLead),
    fitWidth: false,
    tableMinWidth: 1200,
  };

  const scrollShellSx = {
    width: "100%",
    maxWidth: "100%",
    overflowX: "auto",
    overflowY: "visible",
  };

  if (!groupByCreated) {
    return (
      <Box sx={scrollShellSx}>
        <DataTable
          {...tableProps}
          data={leads}
          title="Leads"
          pageSize={25}
          extraActions={groupToggle}
        />
      </Box>
    );
  }

  return (
    <Box sx={scrollShellSx}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        mb={2}
        px={0.5}
      >
        <Title level={5} style={{ margin: 0 }}>
          Leads
        </Title>
        {groupToggle}
      </Box>
      {loading && periodGroups.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={8}
        >
          <CircularProgress />
          <Text type="secondary" style={{ marginTop: 12 }}>
            Loading leads...
          </Text>
        </Box>
      ) : periodGroups.length === 0 ? (
        <Text type="secondary">No leads</Text>
      ) : (
        periodGroups.map((group) => (
          <Box key={group.period} mb={2} sx={scrollShellSx}>
            <DataTable
              {...tableProps}
              data={group.leads}
              title={`${group.label} (${group.leads.length})`}
              showSearch={false}
              showPagination={group.leads.length > 10}
              pageSize={25}
              showRefresh={false}
              extraActions={null}
            />
          </Box>
        ))
      )}
    </Box>
  );
};

export default LeadsTableView;
